import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Stamp, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function LoginPage() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("admin123");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!loading && user) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(username.trim(), password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div dir="rtl" className="min-h-screen flex">
      {/* Right side - form */}
      <div className="flex-1 flex items-center justify-center bg-background px-6 py-10">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-12 h-12 rounded-md bg-primary flex items-center justify-center text-primary-foreground">
              <Stamp className="w-6 h-6" />
            </div>
            <div>
              <div className="font-display font-bold text-xl leading-tight">
                نظام إدارة الجوازات
              </div>
              <div className="text-xs text-muted-foreground">منصة رسمية للموظفين</div>
            </div>
          </div>

          <h1 className="font-display text-4xl font-bold tracking-tight mb-2">
            مرحباً بعودتك
          </h1>
          <p className="text-muted-foreground mb-8">
            سجّل الدخول للوصول إلى لوحة التحكم
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-sm font-medium">اسم المستخدم</Label>
              <Input
                id="username"
                data-testid="login-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin"
                className="h-11 text-base"
                autoFocus
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">كلمة المرور</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-11 text-base"
                required
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="px-3 py-2 rounded-sm border border-destructive/30 bg-destructive/5 text-destructive text-sm"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={submitting}
              className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-medium tracking-wide"
            >
              {submitting && <Loader2 className="w-4 h-4 ms-2 animate-spin" />}
              تسجيل الدخول
            </Button>

            <div className="text-xs text-muted-foreground border-t border-border pt-4 mt-6">
              <div className="font-medium mb-1">حسابات تجريبية:</div>
              <div className="font-mono">admin / admin123 (مدير)</div>
              <div className="font-mono">employee / employee123 (موظف)</div>
            </div>
          </form>
        </div>
      </div>

      {/* Left side - decorative */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute inset-0 gov-stamp opacity-10" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="font-display text-2xl font-bold tracking-tight">
            دائرة الجوازات
          </div>
          <div>
            <div className="font-display text-5xl font-bold leading-tight mb-4">
              توثيق رقمي.<br />دقة رسمية.
            </div>
            <p className="text-primary-foreground/80 max-w-md leading-relaxed">
              منصة موحدة لإدارة استمارات الجوازات، الموظفين، والصلاحيات، مع
              مسح QR وطباعة وثائق رسمية.
            </p>
          </div>
          <div className="text-xs text-primary-foreground/60 font-mono">
            v1.0 — 2026
          </div>
        </div>
      </div>
    </div>
  );
}
